export const formatDate = (date) => {
    if (!date) {
        return '-';
    }

    return new Date(date).toLocaleDateString('pt-BR');
};

export const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const cleanValue = String(value).replace(/[^\d.,-]/g, '');
    const normalizedValue = cleanValue.includes(',') && cleanValue.includes('.')
        ? cleanValue.replace(/\./g, '').replace(',', '.')
        : cleanValue.replace(',', '.');
    const numericValue = Number(normalizedValue);

    if (Number.isNaN(numericValue)) {
        return '-';
    }

    return numericValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });
};

const parseCurrencyNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const cleanValue = String(value).replace(/[^\d.,-]/g, '');
    const normalizedValue = cleanValue.includes(',') && cleanValue.includes('.')
        ? cleanValue.replace(/\./g, '').replace(',', '.')
        : cleanValue.replace(',', '.');
    const numericValue = Number(normalizedValue);

    return Number.isNaN(numericValue) ? null : numericValue;
};

const numberToWords = (number) => {
    const units = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const teens = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const tens = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const hundreds = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

    if (number === 0) {
        return 'zero';
    }

    if (number < 10) {
        return units[number];
    }

    if (number < 20) {
        return teens[number - 10];
    }

    if (number < 100) {
        const ten = Math.floor(number / 10);
        const unit = number % 10;

        return unit ? `${tens[ten]} e ${units[unit]}` : tens[ten];
    }

    if (number === 100) {
        return 'cem';
    }

    if (number < 1000) {
        const hundred = Math.floor(number / 100);
        const rest = number % 100;

        return rest ? `${hundreds[hundred]} e ${numberToWords(rest)}` : hundreds[hundred];
    }

    if (number < 1000000) {
        const thousand = Math.floor(number / 1000);
        const rest = number % 1000;
        const thousandText = thousand === 1 ? 'um mil' : `${numberToWords(thousand)} mil`;

        return rest ? `${thousandText}, ${numberToWords(rest)}` : thousandText;
    }

    const million = Math.floor(number / 1000000);
    const rest = number % 1000000;
    const millionText = million === 1 ? 'um milhão' : `${numberToWords(million)} milhões`;

    return rest ? `${millionText}, ${numberToWords(rest)}` : millionText;
};

export const currencyToWords = (value) => {
    const numericValue = parseCurrencyNumber(value);

    if (numericValue === null) {
        return '-';
    }

    const integerPart = Math.floor(numericValue);
    const cents = Math.round((numericValue - integerPart) * 100);
    const integerWords = numberToWords(integerPart);

    return cents ? `${integerWords} e ${numberToWords(cents)} centavos` : integerWords;
};

export const currencyToWordsParts = (value) => {
    const numericValue = parseCurrencyNumber(value);

    if (numericValue === null) {
        return {
            reais: '-',
            centavos: '',
        };
    }

    const integerPart = Math.floor(numericValue);
    const cents = Math.round((numericValue - integerPart) * 100);

    return {
        reais: numberToWords(integerPart),
        centavos: cents ? ` e ${numberToWords(cents)} centavos` : '',
    };
};

export const formatStatus = (status) => {
    if (!status) {
        return 'Aguardando';
    }

    return String(status)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const displayValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    return value;
};

export const getIntermediaryName = (acquisition) => {
    if (!acquisition) {
        return '-';
    }

    return acquisition.doc_type === 'CNPJ'
        ? acquisition.bussiness_name
        : acquisition.name;
};

export const getAdministratorName = (acquisition) => {
    return acquisition.consortium_agency?.name
        || acquisition.consortium_agency_fullname
        || acquisition.consortium_agency_id
        || '-';
};

export const getAdministratorCnpj = (acquisition) => {
    return acquisition.consortium_agency?.cnpj
        || acquisition.consortium_agency_cnpj
        || '-';
};

export const getAdministratorAddress = (acquisition) => ({
    street: acquisition.consortium_agency?.address || acquisition.consortium_agency_address || '-',
    number: acquisition.consortium_agency?.number || acquisition.consortium_agency_number || '-',
    neighborhood: acquisition.consortium_agency?.neighborhood || acquisition.consortium_agency_neighborhood || '-',
    city: acquisition.consortium_agency?.city || acquisition.consortium_agency_city || '-',
    state: acquisition.consortium_agency?.state || acquisition.consortium_agency_state || '-',
    zipCode: acquisition.consortium_agency?.zipCode || acquisition.consortium_agency_zipcode || '-',
});

export const getBankName = (acquisition) => {
    return acquisition.bank_name
        || acquisition.bank_account?.name
        || acquisition.bank?.name
        || acquisition.bank_account_id
        || '-';
};

export const getAssignorDocument = (acquisition) => {
    return acquisition.doc_type === 'CNPJ'
        ? acquisition.cnpj_number
        : acquisition.cpf_number;
};

export const getReceiptValue = (acquisition) => {
    return acquisition.customer_value
        || acquisition.quotas?.[0]?.receivable_value
        || acquisition.quotas?.[0]?.credit_value
        || '';
};

export const getPaymentPixText = (acquisition) => {
    if (!acquisition.key_pix) {
        return '';
    }

    return `Chave Pix: ${acquisition.key_pix}`;
};

export const getPhone = (acquisition) => {
    return acquisition.phone_number
        || '-';
};

export const getQuotasText = (acquisition) => {
    if (!acquisition.quotas?.length) {
        return ' Cota de Consórcio';
    }

    return ` ${acquisition.quotas
        .map((quota) => [
            `Cota de Consórcio nº ${displayValue(quota.quota_number)} (ou outro número que a substitua)`,
            `do Grupo nº ${displayValue(quota.group_number)}`,
            `Contrato nº ${displayValue(quota.contract_number)}`,
            `com crédito atual no valor de ${formatCurrency(quota.receivable_value)} (${currencyToWords(quota.receivable_value)})`,
        ].join(', '))
        .filter(Boolean)
        .join(', ')},`;
};

export function QuotaLineList({ quotas }) {
    return (
        <div className='intermediary-document-receipt-quotas'>
            {quotas?.length ? (
                quotas.map((quota) => (
                    <div className='intermediary-document-quota-line' key={quota.id}>
                        <span>Grupo: {displayValue(quota.group_number)}</span>
                        <span>- Cota: {displayValue(quota.quota_number)}</span>
                        <span>- Contrato: {displayValue(quota.contract_number)}</span>
                    </div>
                ))
            ) : (
                <p>Nenhuma cota cadastrada.</p>
            )}
        </div>
    );
}
